/**
 * ShipOutScannerModal — 3-step modal for recording boxes loaded onto an outbound transfer.
 *
 * Step 1: Scan/enter transfer barcode → validates transfer exists, loads existing scans.
 * Step 2: Scan employee badge → validates employee via operator service.
 * Step 3: Scan boxes → each scan calls POST immediately (real-time persistence).
 *         Handles 409 duplicate with toast. Undo button calls DELETE. Camera fallback available.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { hteTransferService, ShipoutScanRecord, ShipoutScanSummaryItem } from '../../../services/hteTransferService';
import operatorService from '../../../services/operatorService';
import type { EmployeeDisplayInfo } from '../../../services/operatorService';
import type { HteTransfer } from '../../../services/hteTransferService';
import { isBadgeCode, extractEmployeeNumber } from '../../../utils/barcodeUtils';
import { useBarcodeScanner } from '../../../hooks/useBarcodeScanner';
import CameraScannerModal from '../../../components/CameraScannerModal';
import toast from 'react-hot-toast';
import { X, Scan, Check, ArrowRight, ArrowLeft, Trash2, Camera, User, Package, AlertTriangle, CheckCircle, CheckCircle2, ChevronDown } from 'lucide-react';
import { dailyTransferOutService } from '../../../services/dailyTransferOutService';

interface ShipOutScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional: pre-fill the transfer number (e.g. when opened from a specific transfer row) */
  transferNumber?: string;
  /** Called after scan is confirmed and transfer marked as 'scanned' */
  onConfirmed?: () => void;
  /** Logged-in user's display name for request_by on the transfer */
  requestBy?: string;
  /** Production date for daily-out transfers — enables submit flow after scan confirm */
  productionDate?: string;
}

type Step = 'transfer' | 'badge' | 'boxes';

const ShipOutScannerModal: React.FC<ShipOutScannerModalProps> = ({
  isOpen,
  onClose,
  transferNumber: initialTransferNumber,
  onConfirmed,
  requestBy,
  productionDate,
}) => {
  const { t } = useLanguage();

  // Step state
  const [step, setStep] = useState<Step>('transfer');

  // Step 1: Transfer
  const [transferInput, setTransferInput] = useState('');
  const [transferNumber, setTransferNumber] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [pendingTransfers, setPendingTransfers] = useState<HteTransfer[]>([]);

  // Step 2: Badge
  const [badgeInput, setBadgeInput] = useState('');
  const [employee, setEmployee] = useState<EmployeeDisplayInfo | null>(null);
  const [badgeLoading, setBadgeLoading] = useState(false);

  // Step 3: Boxes
  const [scans, setScans] = useState<ShipoutScanRecord[]>([]);
  const [summary, setSummary] = useState<ShipoutScanSummaryItem[]>([]);
  const [requireConfirmation, setRequireConfirmation] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openSection, setOpenSection] = useState<'boxes' | 'summary'>('boxes');

  // Refs for auto-focus
  const transferInputRef = useRef<HTMLInputElement>(null);
  const badgeInputRef = useRef<HTMLInputElement>(null);
  const boxInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (initialTransferNumber) {
        // Pre-fill and auto-validate
        setTransferInput(initialTransferNumber);
        setBadgeInput('');
        setEmployee(null);
        setScans([]);
        setSummary([]);
        setManualConfirmed(new Set());
        validateTransfer(initialTransferNumber);
      } else {
        setStep('transfer');
        setTransferInput('');
        setTransferNumber('');
        setEmployee(null);
        setScans([]);
        setSummary([]);
        setPendingTransfers([]);
        setTimeout(() => transferInputRef.current?.focus(), 100);
        // Fetch pending transfers for the picker list
        hteTransferService.getTransfers({ status: 'pending', limit: 20 }).then(res => {
          setPendingTransfers(res.transfers);
        }).catch((err) => {
          console.warn('Failed to load pending transfers for ship-out picker', err);
          toast.error(t('messages.loadFailed').replace('{item}', 'pending transfers'));
        });
      }
    }
  }, [isOpen, initialTransferNumber]);

  // Auto-focus when step changes
  useEffect(() => {
    if (step === 'transfer') {
      setTimeout(() => transferInputRef.current?.focus(), 100);
    } else if (step === 'badge') {
      setTimeout(() => badgeInputRef.current?.focus(), 100);
    } else if (step === 'boxes') {
      setTimeout(() => boxInputRef.current?.focus(), 100);
    }
  }, [step]);

  // Step 1: Validate transfer number
  const validateTransfer = async (number: string) => {
    const trimmed = number.trim();
    if (!trimmed) return;

    setTransferLoading(true);
    try {
      // Try to load existing scans + summary — this validates the transfer exists via backend
      const result = await hteTransferService.getShipoutScans(trimmed);
      setTransferNumber(trimmed);
      setScans(result.scans);
      setSummary(result.summary);
      setRequireConfirmation(result.require_confirmation);
      setStep('badge');
    } catch (err: any) {
      if (err?.response?.status === 404) {
        toast.error(t('shipOutScan.transferNotFound'));
      } else {
        toast.error(err?.response?.data?.message || t('shipOutScan.transferNotFound'));
      }
    } finally {
      setTransferLoading(false);
    }
  };

  // Step 2: Validate badge
  const validateBadge = async (barcode: string) => {
    const trimmed = barcode.trim();
    if (!trimmed) return;

    let employeeNumber: string;
    if (isBadgeCode(trimmed)) {
      employeeNumber = extractEmployeeNumber(trimmed);
    } else {
      // Allow raw employee number too
      employeeNumber = trimmed;
    }

    setBadgeLoading(true);
    try {
      const info = await operatorService.getEmployee(employeeNumber);
      if (!info) {
        toast.error(t('shipOutScan.employeeNotFound'));
        return;
      }
      setEmployee(info);
      setStep('boxes');
    } catch (err: any) {
      toast.error(t('shipOutScan.employeeNotFound'));
    } finally {
      setBadgeLoading(false);
    }
  };

  // Helper: refresh scans + summary from backend
  const refreshScans = useCallback(async () => {
    if (!transferNumber) return;
    try {
      const result = await hteTransferService.getShipoutScans(transferNumber);
      setScans(result.scans);
      setSummary(result.summary);
      setRequireConfirmation(result.require_confirmation);
    } catch { /* ignore */ }
  }, [transferNumber]);

  // Step 3: Handle box scan (called by useBarcodeScanner or camera)
  const handleBoxScan = useCallback(async (barcode: string) => {
    if (!transferNumber || !employee) return;

    try {
      await hteTransferService.recordShipoutScan(
        transferNumber,
        barcode,
        employee.employee_number
      );
      toast.success(t('shipOutScan.boxScanned'));
      await refreshScans();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        toast.error(t('shipOutScan.duplicateBox'));
      } else {
        toast.error(err?.response?.data?.message || 'Failed to record scan');
      }
    }
  }, [transferNumber, employee, t, refreshScans]);

  // Barcode scanner hook — active only on step 3
  const {
    manualEntry,
    setManualEntry,
    submitManualEntry,
  } = useBarcodeScanner({
    onScan: handleBoxScan,
    enabled: isOpen && step === 'boxes',
    minLength: 3,
  });

  // Undo a scan — delete then refresh scans + summary from backend
  const handleUndoScan = async (scanId: string) => {
    try {
      await hteTransferService.undoShipoutScan(transferNumber, scanId);
      toast.success(t('shipOutScan.scanRemoved'));
      await refreshScans();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to remove scan');
    }
  };

  // Track manual confirmations for remaining materials without barcodes
  const [manualConfirmed, setManualConfirmed] = useState<Set<string>>(new Set());

  // Handle manual confirmation for remaining materials without barcodes
  // Build a unique key for manual confirmation tracking
  const manualConfirmKey = (row: ShipoutScanSummaryItem) =>
    `${row.item_number}::${row.remaining_type || row.category}`;

  const handleManualConfirm = async (row: ShipoutScanSummaryItem) => {
    if (!transferNumber || !employee) return;
    try {
      if (row.category.startsWith('by_product')) {
        // By-products: look up real BP-* barcodes from production_inventory
        await hteTransferService.confirmByProductPalette(transferNumber, employee.employee_number, row.item_number);
      } else {
        await hteTransferService.recordManualShipoutWeight(
          transferNumber,
          row.item_number,
          row.expected_weight,
          employee.employee_number,
          row.remaining_type || row.category
        );
        setManualConfirmed(prev => new Set(prev).add(manualConfirmKey(row)));
      }
      toast.success(t('shipOutScan.manualConfirm'));
      await refreshScans();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to confirm');
    }
  };

  // Check if all items are fully scanned/confirmed
  const allScanned = summary.length > 0 && summary.every(row => {
    if (!row.has_barcode && row.category !== 'finished_product') {
      // No barcode (remaining materials, by-products, rejects): needs manual confirmation
      return manualConfirmed.has(manualConfirmKey(row)) || row.scanned_count > 0;
    }
    // Scannable items: need qty and weight match
    const qtyMatch = row.scanned_count >= row.expected_quantity;
    const wtMatch = row.expected_weight <= 0 || row.scanned_weight >= row.expected_weight;
    return qtyMatch && wtMatch;
  });

  // Confirm scan — marks the transfer as 'scanned'
  const handleConfirmScan = async () => {
    if (!transferNumber || !employee) return;
    setConfirming(true);
    try {
      await hteTransferService.confirmShipoutScan(transferNumber, employee.employee_number, requestBy);
      toast.success(t('shipOutScan.scanConfirmed'));
      // For daily-out transfers, show submit confirmation instead of closing
      if (productionDate) {
        setShowSubmitConfirm(true);
      } else {
        onConfirmed?.();
        onClose();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to confirm scan');
    } finally {
      setConfirming(false);
    }
  };

  // Submit transfer — creates ICS rows and marks transfer as pending
  const handleSubmitTransfer = async () => {
    if (!productionDate) return;
    setSubmitting(true);
    try {
      await dailyTransferOutService.submitTransferOut(productionDate, employee?.employee_number);
      toast.success(t('dailyTransferOut.submitSuccess') || 'Transfer submitted successfully');
      onConfirmed?.();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('dailyTransferOut.submitFailed') || 'Failed to submit transfer');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Step indicator component
  const steps: { key: Step; label: string; icon: React.ReactNode }[] = [
    { key: 'transfer', label: t('shipOutScan.scanTransfer'), icon: <Package className="w-4 h-4" /> },
    { key: 'badge', label: t('shipOutScan.scanBadge'), icon: <User className="w-4 h-4" /> },
    { key: 'boxes', label: t('shipOutScan.scanBoxes'), icon: <Scan className="w-4 h-4" /> },
  ];

  const stepIndex = steps.findIndex(s => s.key === step);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full mx-4 max-h-[90vh] flex flex-col ${step === 'boxes' ? 'max-w-2xl' : 'max-w-lg'}`}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              {step !== 'transfer' && (
                <button
                  onClick={() => {
                    if (step === 'boxes') { setStep('badge'); setEmployee(null); setBadgeInput(''); }
                    else if (step === 'badge') { setStep('transfer'); setTransferNumber(''); setScans([]); }
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('shipOutScan.title')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-start">
              {steps.map((s, i) => (
                <React.Fragment key={s.key}>
                  {i > 0 && (
                    <div className={`flex-1 h-0.5 mt-4 rounded-full transition-colors ${i <= stepIndex ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-600'}`} />
                  )}
                  <div className="flex flex-col items-center gap-1 w-20">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                        i < stepIndex
                          ? 'bg-green-500 text-white'
                          : i === stepIndex
                          ? 'bg-primary-600 text-white ring-2 ring-primary-300 dark:ring-primary-700'
                          : 'bg-gray-200 text-gray-400 dark:bg-gray-600 dark:text-gray-500'
                      }`}
                    >
                      {i < stepIndex ? <Check className="w-4 h-4" /> : s.icon}
                    </div>
                    <span className={`text-[10px] font-medium text-center leading-tight ${
                      i <= stepIndex ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'
                    }`}>{s.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Sticky info bar + scan input (Step 3 only) */}
          {step === 'boxes' && (
            <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700 space-y-3">
              <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2 flex items-center justify-between">
                <div>
                  Transfer: <span className="font-mono font-medium">{transferNumber}</span>
                  {employee && (
                    <span className="ml-3">
                      {t('shipOutScan.scannedBy')}: <span className="font-medium">{employee.display_name}</span>
                    </span>
                  )}
                </div>
                <div className="text-primary-600 dark:text-primary-400 font-semibold">
                  {t('shipOutScan.totalScanned')}: {scans.length}
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  ref={boxInputRef}
                  type="text"
                  inputMode="none"
                  onTouchStart={(e) => (e.currentTarget.inputMode = 'text')}
                  value={manualEntry}
                  onChange={(e) => setManualEntry(e.target.value)}
                  placeholder={t('shipOutScan.scanBoxBarcode')}
                  className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  onClick={() => setShowCamera(true)}
                  className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  title={t('cameraScanner.openCamera')}
                >
                  <Camera className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">

            {/* ===== Step 1: Transfer ===== */}
            {step === 'transfer' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('shipOutScan.step')} 1: {t('shipOutScan.scanTransfer')}
                </p>
                <div className="flex gap-2">
                  <input
                    ref={transferInputRef}
                    type="text"
                    inputMode="none"
                    onTouchStart={(e) => (e.currentTarget.inputMode = 'text')}
                    value={transferInput}
                    onChange={(e) => setTransferInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = (e.target as HTMLInputElement).value;
                        validateTransfer(val || transferInput);
                      }
                    }}
                    placeholder={t('shipOutScan.enterTransferNumber')}
                    className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={transferLoading}
                  />
                  <button
                    onClick={() => validateTransfer(transferInput)}
                    disabled={!transferInput.trim() || transferLoading}
                    className="px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {transferLoading ? '...' : <ArrowRight className="w-4 h-4" />}
                  </button>
                </div>
                {/* Pending transfers quick-pick list */}
                {pendingTransfers.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      {t('shipOutScan.orSelectTransfer')}
                    </p>
                    <div className="max-h-[30vh] overflow-y-auto space-y-1">
                      {pendingTransfers.map(tr => (
                        <button
                          key={tr.transfer_number}
                          onClick={() => validateTransfer(tr.transfer_number)}
                          disabled={transferLoading}
                          className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:border-primary-300 dark:hover:border-primary-700 transition-colors disabled:opacity-50"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-mono font-medium text-gray-900 dark:text-white">{tr.transfer_number}</span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {tr.transfer_date ? new Date(tr.transfer_date).toLocaleDateString() : ''}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {tr.warehouse_code_from}{tr.location_code_from ? `/${tr.location_code_from}` : ''} → {tr.warehouse_code_to}{tr.location_code_to ? `/${tr.location_code_to}` : ''}
                            {tr.line_count != null && <span className="ml-2">({tr.line_count} items)</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ===== Step 2: Badge ===== */}
            {step === 'badge' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('shipOutScan.step')} 2: {t('shipOutScan.scanBadge')}
                </p>
                <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                  Transfer: <span className="font-mono font-medium">{transferNumber}</span>
                  {scans.length > 0 && (
                    <span className="ml-2">({scans.length} existing scans)</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    ref={badgeInputRef}
                    type="text"
                    inputMode="none"
                    onTouchStart={(e) => (e.currentTarget.inputMode = 'text')}
                    value={badgeInput}
                    onChange={(e) => setBadgeInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        // Read directly from input element — state may lag behind scanner speed
                        const val = (e.target as HTMLInputElement).value;
                        validateBadge(val || badgeInput);
                      }
                    }}
                    placeholder={t('shipOutScan.enterBadge')}
                    className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={badgeLoading}
                  />
                  <button
                    onClick={() => validateBadge(badgeInput)}
                    disabled={!badgeInput.trim() || badgeLoading}
                    className="px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {badgeLoading ? '...' : <ArrowRight className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            {/* ===== Step 3: Scan Boxes — two-column layout ===== */}
            {step === 'boxes' && (
              <div className="flex flex-col gap-4">
                {/* Accordion: Scan Boxes */}
                <div className="flex flex-col">
                  <button
                    onClick={() => setOpenSection(openSection === 'boxes' ? 'summary' : 'boxes')}
                    className="flex items-center justify-between py-2 group"
                  >
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${openSection === 'boxes' ? '' : '-rotate-90'}`} />
                      <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                        {t('shipOutScan.scanBoxes')} {scans.length > 0 && `(${scans.length})`}
                      </h4>
                    </div>
                    {scans.length > 0 && openSection === 'boxes' && (
                      <span
                        onClick={(e) => { e.stopPropagation(); setShowClearConfirm(true); }}
                        className="text-[10px] text-red-500 hover:text-red-700 dark:hover:text-red-400"
                      >
                        {t('shipOutScan.clearAll')}
                      </span>
                    )}
                  </button>
                  {openSection === 'boxes' && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg flex flex-col">
                    {/* Fixed header */}
                    <table className="w-full text-sm table-fixed">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="w-8 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">#</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Item</th>
                          <th className="w-20 px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Weight</th>
                          <th className="w-8 px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"></th>
                        </tr>
                      </thead>
                    </table>
                    {/* Scrollable body */}
                    <div className="max-h-[30vh] overflow-y-auto">
                      {scans.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                          {t('shipOutScan.noScansYet')}
                        </div>
                      ) : (
                        <table className="w-full text-sm table-fixed">
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {scans.map((scan, idx) => {
                              // Extract remaining_type from manual barcode for display
                              const manualRt = scan.barcode.startsWith('MANUAL-')
                                ? (['unused_lot', 'remainder_intact', 'remainder_not_intact'] as const).find(rt => scan.barcode.includes(`-${rt}-`))
                                : undefined;
                              const rtLabel = manualRt === 'unused_lot' ? t('shipOutScan.unusedLot')
                                : manualRt === 'remainder_intact' ? t('shipOutScan.remainingIntact')
                                : manualRt === 'remainder_not_intact' ? t('shipOutScan.remainingNotIntact')
                                : undefined;
                              return (
                              <tr key={scan.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <td className="w-8 px-3 py-2 text-gray-400 dark:text-gray-500">{scans.length - idx}</td>
                                <td className="px-3 py-2">
                                  <div className="text-gray-900 dark:text-gray-100 text-xs font-medium truncate">
                                    {scan.item_number || '-'}
                                    {scan.item_english_name && <span className="font-normal"> - {scan.item_english_name}</span>}
                                    {scan.item_chinese_name && <span className="font-normal text-gray-400 dark:text-gray-500"> ({scan.item_chinese_name})</span>}
                                  </div>
                                  <div className="text-[10px] truncate">
                                    {scan.barcode.startsWith('MANUAL-') ? (
                                      <>
                                        <span className="text-green-600 dark:text-green-400 font-medium">{t('shipOutScan.manualConfirm')}</span>
                                        {rtLabel && <span className={`ml-1 font-medium ${manualRt === 'remainder_not_intact' ? 'text-orange-500 dark:text-orange-400' : 'text-purple-500 dark:text-purple-400'}`}> · {rtLabel}</span>}
                                      </>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500 font-mono">{scan.barcode}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="w-20 px-3 py-2 text-right text-gray-700 dark:text-gray-300 text-xs">
                                  {scan.actual_weight != null ? `${Number(scan.actual_weight).toFixed(2)} lb` : '-'}
                                </td>
                                <td className="w-8 px-3 py-2 text-right">
                                  <button
                                    onClick={() => handleUndoScan(scan.id)}
                                    className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 flex items-center gap-1 ml-auto"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                  )}
                </div>

                {/* Accordion: Expected vs Scanned summary */}
                <div>
                  <button
                    onClick={() => setOpenSection(openSection === 'summary' ? 'boxes' : 'summary')}
                    className="flex items-center gap-2 py-2 group"
                  >
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${openSection === 'summary' ? '' : '-rotate-90'}`} />
                    <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                      {t('shipOutScan.summaryTitle')}
                    </h4>
                  </button>
                  {openSection === 'summary' && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg flex flex-col">
                    {/* Fixed header */}
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">{t('shipOutScan.item')}</th>
                          <th className="w-16 px-2 py-2 text-right font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{t('shipOutScan.expectedQty')}</th>
                          <th className="w-16 px-2 py-2 text-right font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{t('shipOutScan.scannedQty')}</th>
                          <th className="w-16 px-2 py-2 text-right font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{t('shipOutScan.expectedWt')}</th>
                          <th className="w-16 px-2 py-2 text-right font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{t('shipOutScan.scannedWt')}</th>
                          <th className="w-8 px-2 py-2 text-center font-medium text-gray-500 dark:text-gray-400"></th>
                        </tr>
                      </thead>
                    </table>
                    {/* Scrollable body */}
                    <div className="max-h-[30vh] overflow-y-auto">
                    {summary.length > 0 ? (
                      <table className="w-full text-xs">
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {summary.map((row) => {
                            const isRemaining = row.category === 'remaining_material';
                            const isByProduct = row.category.startsWith('by_product');
                            const isRejected = row.category === 'rejected_material';
                            const needsManualConfirm = !row.has_barcode && row.category !== 'finished_product';

                            let allMatch: boolean;
                            let qtyMatch = true;
                            let wtMatch = true;
                            if (needsManualConfirm) {
                              allMatch = manualConfirmed.has(`${row.item_number}::${row.remaining_type || row.category}`) || row.scanned_count > 0;
                            } else {
                              qtyMatch = row.scanned_count >= row.expected_quantity;
                              wtMatch = row.expected_weight <= 0 || row.scanned_weight >= row.expected_weight;
                              allMatch = qtyMatch && wtMatch;
                            }

                            // Category label and color
                            let categoryLabel: string | undefined;
                            let categoryColor = '';
                            if (isRemaining) {
                              categoryLabel = row.remaining_type === 'unused_lot'
                                ? t('shipOutScan.unusedLot')
                                : row.remaining_type === 'remainder_intact'
                                ? t('shipOutScan.remainingIntact')
                                : row.remaining_type === 'remainder_not_intact'
                                ? t('shipOutScan.remainingNotIntact')
                                : t('shipOutScan.remainingMaterial');
                              categoryColor = row.remaining_type === 'remainder_not_intact'
                                ? 'text-orange-500 dark:text-orange-400'
                                : 'text-purple-500 dark:text-purple-400';
                            } else if (isByProduct) {
                              categoryLabel = row.category === 'by_product_marketable'
                                ? t('shipOutScan.byProductMarketable')
                                : row.category === 'by_product_production'
                                ? t('shipOutScan.byProductProduction')
                                : t('shipOutScan.byProduct');
                              categoryColor = 'text-blue-500 dark:text-blue-400';
                            } else if (isRejected) {
                              categoryLabel = t('shipOutScan.rejectedMaterial');
                              categoryColor = 'text-red-500 dark:text-red-400';
                            }

                            return (
                              <tr
                                key={`${row.item_number}-${row.remaining_type || row.category}`}
                                className={allMatch ? '' : 'bg-amber-50/50 dark:bg-amber-900/10'}
                              >
                                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                  <div className="font-medium">{row.item_number}</div>
                                  {row.item_english_name && (
                                    <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate max-w-[100px]">{row.item_english_name}</div>
                                  )}
                                  {categoryLabel && (
                                    <div className={`text-[10px] font-medium ${categoryColor}`}>
                                      {categoryLabel}
                                    </div>
                                  )}
                                </td>
                                {needsManualConfirm ? (
                                  <>
                                    <td colSpan={3} className="px-2 py-2 text-center">
                                      {allMatch ? (
                                        <span className="text-[10px] text-green-600 dark:text-green-400 font-medium">{t('shipOutScan.manualConfirm')}</span>
                                      ) : (
                                        <button
                                          onClick={() => handleManualConfirm(row)}
                                          className="px-2 py-1 text-[10px] font-medium text-white bg-orange-500 hover:bg-orange-600 rounded transition-colors"
                                        >
                                          {t('shipOutScan.confirmOnPalette')}
                                        </button>
                                      )}
                                    </td>
                                    <td className="px-2 py-2 text-right font-medium text-gray-600 dark:text-gray-400">
                                      {row.expected_weight > 0 ? `${Number(row.expected_weight).toFixed(2)} lb` : '-'}
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td className="w-16 px-2 py-2 text-right text-gray-700 dark:text-gray-300">{row.expected_quantity}</td>
                                    <td className={`w-16 px-2 py-2 text-right font-medium ${qtyMatch ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                      {row.scanned_count}
                                    </td>
                                    <td className="w-16 px-2 py-2 text-right text-gray-700 dark:text-gray-300">
                                      {row.expected_weight > 0 ? `${Number(row.expected_weight).toFixed(2)}` : '-'}
                                    </td>
                                    <td className={`w-16 px-2 py-2 text-right font-medium ${wtMatch ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                      {row.scanned_weight > 0 ? `${Number(row.scanned_weight).toFixed(2)}` : '-'}
                                    </td>
                                  </>
                                )}
                                <td className="px-2 py-2 text-center">
                                  {allMatch ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-500 inline-block" />
                                  ) : (
                                    <AlertTriangle className="w-4 h-4 text-amber-500 inline-block" />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                        {t('shipOutScan.noScansYet')}
                      </div>
                    )}
                    </div>
                  </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              {step === 'boxes' && scans.length > 0 && allScanned && (
                <button
                  onClick={handleConfirmScan}
                  disabled={confirming}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg transition-colors"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {confirming ? t('common.processing') : t('shipOutScan.confirmScan')}
                </button>
              )}
          </div>
        </div>
      </div>

      {/* Camera scanner overlay */}
      <CameraScannerModal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onScan={(barcode) => {
          setShowCamera(false);
          handleBoxScan(barcode);
        }}
      />

      {/* Clear all scans confirmation modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowClearConfirm(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('shipOutScan.clearAll')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t('shipOutScan.clearAllConfirm')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={async () => {
                  setShowClearConfirm(false);
                  try {
                    await hteTransferService.clearShipoutScans(transferNumber);
                    setManualConfirmed(new Set());
                    toast.success(t('shipOutScan.clearAllSuccess'));
                    await refreshScans();
                  } catch (err: any) {
                    toast.error(err?.response?.data?.message || 'Failed to clear scans');
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                {t('shipOutScan.clearAll')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit transfer confirmation dialog (daily-out only) */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSubmitConfirm(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('dailyTransferOut.submitTransfer') || 'Submit Transfer'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {t('dailyTransferOut.confirmSubmit') || 'This will create the ICS transfer document and make it visible to the warehouse. Continue?'}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowSubmitConfirm(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSubmitTransfer}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {submitting ? t('common.processing') : t('common.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShipOutScannerModal;
